import ky from 'ky';
import Notification from '@Common/Notification';
import { SUBSCRIPTION_HEADER_NAME } from '@Constants/constants';
import { API_ROOT, API_TIMEOUT } from '@Helpers/apiConfig';
import { TOKEN_REFRESH_SUCCESS, LOGOUT_SUCCESS } from '@Constants/actionTypes';

// Transient failures worth retrying; every other error status fails on the first attempt
const RETRYABLE_STATUS_CODES = [404, 408, 429, 504];

let _store;

export const pacerApiInjectStore = store => {
    _store = store;
}

// Shared refresh promise — prevents concurrent token refresh calls
let _refreshPromise: Promise<string | null> | null = null;

const refreshAccessToken = (): Promise<string | null> => {
    if (_refreshPromise) return _refreshPromise;

    const { refreshToken, isSSO } = _store.getState().user;

    if (!refreshToken) {
        _store.dispatch({ type: LOGOUT_SUCCESS });
        return Promise.resolve(null);
    }

    _refreshPromise = fetch(`${API_ROOT}account/refresh-token`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ refreshToken, isSSO }),
    })
        .then(async (resp) => {
            if (!resp.ok) throw new Error('Token refresh failed');
            const data = await resp.json();
            _store.dispatch({ type: TOKEN_REFRESH_SUCCESS, payload: data });
            return data.accessToken as string;
        })
        .catch(() => {
            _store.dispatch({ type: LOGOUT_SUCCESS });
            return null;
        })
        .finally(() => {
            _refreshPromise = null;
        });

    return _refreshPromise;
};

export const createPacerApi = () => {
    if (!_store) {
        throw new Error(
            'pacerApi: Store has not been injected. Please call pacerApiInjectStore(store) before using createPacerApi().'
        );
    }
    const state = _store.getState();
    const token = state.user.accessToken;
    const subscriptionId = state.user.currentSubscriptionId;

    return ky.extend({
        timeout: API_TIMEOUT,
        retry: {
            limit: 2, // Retries 2 times (after initial failure)
            statusCodes: RETRYABLE_STATUS_CODES,
            // delay: 1000, // Constant 1 second delay (overrides exponential)
            // backoffLimit: 30000, // Max delay of 30 seconds
            // jitter: true, // Add random jitter
            retryOnTimeout: true,
        },
        throwHttpErrors: false, // We'll handle HTTP errors manually (showing notifications, etc.)
        hooks: {
            beforeRequest: [
                (request, options, { retryCount }) => {
                    // Only set default auth header on initial request, not on retries (retries may have refreshed token set by beforeRetry)
                    if (retryCount === 0) {
                        request.headers.set('Authorization', `Bearer ${token}`);
                    }

                    if (subscriptionId) {
                        request.headers.set(SUBSCRIPTION_HEADER_NAME, subscriptionId.toString());
                    }
                }
            ],
            afterResponse: [
                // Retry with a fresh token on a 401 error; log out if refresh fails
                async (request, _options, response, state) => {
                    if (response.status === 401 && state.retryCount === 0) {
                        const newToken = await refreshAccessToken();
                        if (newToken) {
                            const headers = new Headers(request.headers);
                            headers.set('Authorization', `Bearer ${newToken}`);
                            console.log('asd-ret')
                            return fetch(new Request(request, { headers }));
                        }
                        // Refresh failed: LOGOUT_SUCCESS was dispatched, PrivateRoute handles redirect
                    }
                },

                // Show a notification for non-success status codes (401 is handled above)
                async (request, options, response, state) => {
                    if (response.status === 401 || response.ok) return;

                    /**
                     * Wait for the final attempt only when this status is one ky actually
                     * retries. Anything else (400, 403, 500, …) fails on the first attempt,
                     * so gating on retryCount would silence it completely.
                     */
                    const retry = options && options.retry;
                    const maxRetries = (retry && typeof retry.limit === 'number') ? retry.limit : 2;
                    const willRetry = RETRYABLE_STATUS_CODES.includes(response.status) && state.retryCount < maxRetries;
                    if (willRetry) return;

                    console.error(`API request error: ${request.method} ${request.url} - Status: ${response.status} - Retry Count: ${state.retryCount}`);

                    let body: Record<string, unknown> = {};
                    try {
                        body = await response.clone().json();
                    } catch {
                        // Non-JSON error body (HTML error page, empty response, …)
                    }
                    const message = typeof body.message === 'string' && body.message
                        ? body.message
                        : 'An unexpected error occurred.';
                    Notification('error', 'An Error Occurred', message);

                    /**
                     * Stamp the failure into the body. parseMutationResponse only ever sees the
                     * parsed body, so an error payload that carries neither `isError` nor `status`
                     * (e.g. `{ message: "..." }` on a 400) would otherwise be read as a success —
                     * reporting "saved" for a request the server rejected.
                     */
                    return new Response(
                        JSON.stringify({
                            ...body,
                            isError: true,
                            status: response.status,
                            errors: Array.isArray(body.errors) && body.errors.length ? body.errors : [{ message }],
                        }),
                        { status: response.status, headers: { 'Content-Type': 'application/json' } }
                    );
                }
            ]
        }
    });
}
